(() => {
  const COMPONENT_SELECTOR = 'collection-component[data-section-type="collection"]';
  const GRID_SELECTOR = '[data-products-grid] .grid';
  const PRODUCT_CARD_SELECTOR = 'grid-item.product-item';
  const FEATURE_TILE_SELECTOR = '.product-item--featured-image';
  const MOBILE_BREAKPOINT = 750;
  const layouts = new WeakMap();

  class CollectionProductGridLayout {
    constructor(component) {
      this.component = component;
      this.grid = null;
      this.frame = null;
      this.gridWidth = 0;
      this.resizeObserver = new ResizeObserver((entries) => {
        const gridWidth = entries[0]?.contentRect.width;

        if (gridWidth !== this.gridWidth) {
          this.gridWidth = gridWidth;
          this.scheduleUpdate();
        }
      });
      this.mutationObserver = new MutationObserver(() => {
        this.connectGrid();
        this.scheduleUpdate();
      });

      this.mutationObserver.observe(this.component, { childList: true, subtree: true });
      this.connectGrid();
    }

    connectGrid() {
      const grid = this.component.querySelector(GRID_SELECTOR);

      if (grid === this.grid) {
        return;
      }

      this.resizeObserver.disconnect();
      this.grid = grid;

      if (this.grid) {
        this.gridWidth = this.grid.getBoundingClientRect().width;
        this.resizeObserver.observe(this.grid);
        this.scheduleUpdate();
      }
    }

    scheduleUpdate() {
      if (this.frame) {
        cancelAnimationFrame(this.frame);
      }

      this.frame = requestAnimationFrame(() => this.update());
    }

    update() {
      this.frame = null;

      if (!this.grid?.isConnected) {
        this.connectGrid();
        return;
      }

      const productCard = this.grid.querySelector(PRODUCT_CARD_SELECTOR);
      const featureTiles = this.grid.querySelectorAll(FEATURE_TILE_SELECTOR);

      if (!productCard || featureTiles.length === 0) {
        return;
      }

      for (const featureTile of featureTiles) {
        featureTile.style.removeProperty('--featured-tile-min-height');
      }

      this.frame = requestAnimationFrame(() => {
        this.frame = null;

        const productCardHeight = productCard.getBoundingClientRect().height;
        const rowGap = Number.parseFloat(getComputedStyle(this.grid).rowGap) || 0;

        for (const featureTile of featureTiles) {
          const featureTileStyle = getComputedStyle(featureTile);
          const rowsSetting =
            window.innerWidth < MOBILE_BREAKPOINT
              ? featureTileStyle.getPropertyValue('--featured-rows-mobile')
              : featureTileStyle.getPropertyValue('--featured-rows');
          const rows = Math.max(1, Number.parseInt(rowsSetting, 10) || 1);
          const featureTileHeight = productCardHeight * rows + rowGap * (rows - 1);

          featureTile.style.setProperty('--featured-tile-min-height', `${featureTileHeight}px`);
        }
      });
    }
  }

  const initializeLayouts = (root = document) => {
    const components = root.matches?.(COMPONENT_SELECTOR)
      ? [root]
      : root.querySelectorAll?.(COMPONENT_SELECTOR) || [];

    for (const component of components) {
      if (!layouts.has(component)) {
        layouts.set(component, new CollectionProductGridLayout(component));
      }
    }
  };

  initializeLayouts();

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const addedNode of mutation.addedNodes) {
        if (addedNode.nodeType === Node.ELEMENT_NODE) {
          initializeLayouts(addedNode);
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
